import React from "react";
import styled, { keyframes } from "styled-components";
import { MdCheckCircle, MdDeviceHub, MdFlashOn, MdMonetizationOn } from "react-icons/md";
import theme from "theme";
import { proofLoop, sectionIds } from "content/site";
import { Container, Section } from "components/marketing/primitives";

const pulse = keyframes`
	0%, 100% { opacity: 0.4; }
	50% { opacity: 1; }
`;

const ProofSection = styled(Section)`
	position: relative;
	overflow: hidden;
	padding-top: clamp(2.25rem, 4.5vw, 3.25rem);
	padding-bottom: clamp(2.25rem, 4.5vw, 3.25rem);

	&::before {
		content: "";
		position: absolute;
		inset: 0;
		background:
			radial-gradient(ellipse 70% 50% at 20% 0%, rgba(0, 119, 204, 0.18) 0%, transparent 55%),
			radial-gradient(ellipse 60% 40% at 90% 100%, rgba(0, 119, 204, 0.14) 0%, transparent 50%);
		pointer-events: none;
	}
`;

const Inner = styled(Container)`
	position: relative;
	z-index: 1;
`;

const Header = styled.div`
	margin-bottom: 1.75rem;
	max-width: 40rem;
`;

const Eyebrow = styled.p`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.75rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.1em;
	color: ${theme.color.primary};
	margin: 0 0 0.5rem;
`;

const Title = styled.h2`
	font-family: ${theme.fontFamily.sans};
	font-size: clamp(1.5rem, 3.5vw, 2.125rem);
	font-weight: 800;
	line-height: 1.12;
	color: #fff;
	margin: 0 0 0.65rem;
	letter-spacing: -0.03em;
`;

const Lead = styled.p`
	font-family: ${theme.fontFamily.sans};
	font-size: 1rem;
	line-height: 1.55;
	color: ${theme.color.onDarkMuted};
	margin: 0;
`;

const Pipeline = styled.div`
	position: relative;
`;

const FlowRail = styled.div`
	display: none;
	@media (min-width: 900px) {
		display: block;
		position: absolute;
		top: 2.35rem;
		left: 8%;
		right: 8%;
		height: 3px;
		border-radius: 999px;
		background: linear-gradient(
			90deg,
			rgba(0, 119, 204, 0.15) 0%,
			rgba(0, 119, 204, 0.45) 35%,
			rgba(0, 119, 204, 0.9) 52%,
			rgba(0, 119, 204, 0.45) 68%,
			rgba(0, 119, 204, 0.15) 100%
		);
		box-shadow: 0 0 24px rgba(0, 119, 204, 0.25);

		&::after {
			content: "";
			position: absolute;
			top: 50%;
			left: 0;
			width: 28%;
			height: 3px;
			transform: translateY(-50%);
			border-radius: 999px;
			background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.85), transparent);
			animation: ${pulse} 2.8s ease-in-out infinite;

			@media (prefers-reduced-motion: reduce) {
				animation: none;
				opacity: 0.6;
			}
		}
	}
`;

const Steps = styled.ol`
	list-style: none;
	margin: 0;
	padding: 0;
	display: grid;
	grid-template-columns: 1fr;
	gap: 1rem;

	@media (min-width: 700px) {
		grid-template-columns: repeat(2, 1fr);
		gap: 1.15rem;
	}

	@media (min-width: 900px) {
		grid-template-columns: repeat(4, 1fr);
		gap: 1rem;
		padding-top: 0.25rem;
	}
`;

const StepCard = styled.li`
	position: relative;
	display: flex;
	flex-direction: column;
	min-height: 100%;
	padding: 1.35rem 1.25rem 1.4rem;
	border-radius: ${theme.radius.lg};
	border: 1px solid ${(p) => (p.$featured ? "rgba(0, 119, 204, 0.55)" : "rgba(255, 255, 255, 0.1)")};
	background: ${(p) =>
		p.$featured
			? "linear-gradient(165deg, rgba(0, 119, 204, 0.2) 0%, rgba(14, 19, 23, 0.92) 42%, rgba(14, 19, 23, 0.98) 100%)"
			: "linear-gradient(165deg, rgba(255, 255, 255, 0.07) 0%, rgba(14, 19, 23, 0.85) 100%)"};
	box-shadow: ${(p) =>
		p.$featured
			? "0 0 0 1px rgba(0, 119, 204, 0.15), 0 16px 40px -12px rgba(0, 119, 204, 0.35)"
			: "0 8px 24px -12px rgba(0, 0, 0, 0.45)"};
	backdrop-filter: blur(8px);
	transition:
		transform ${theme.transitionDuration.fast} ${theme.transitionTimingFunction.easeOut},
		border-color ${theme.transitionDuration.fast} ${theme.transitionTimingFunction.easeOut},
		box-shadow ${theme.transitionDuration.fast} ${theme.transitionTimingFunction.easeOut};

	@media (min-width: 900px) {
		${(p) =>
			p.$featured &&
			`
			transform: translateY(-6px);
			z-index: 2;
		`}
	}

	&:hover {
		transform: translateY(-4px);
		border-color: ${(p) => (p.$featured ? "rgba(0, 119, 204, 0.75)" : "rgba(0, 119, 204, 0.45)")};
		box-shadow: ${(p) =>
			p.$featured
				? "0 0 0 1px rgba(0, 119, 204, 0.25), 0 20px 48px -10px rgba(0, 119, 204, 0.4)"
				: "0 16px 32px -12px rgba(0, 119, 204, 0.25)"};
	}

	@media (prefers-reduced-motion: reduce) {
		transition: border-color 0.15s ease, box-shadow 0.15s ease;
		&:hover {
			transform: none;
		}
	}

	@media (max-width: 899px) {
		padding-left: 1.35rem;

		&::before {
			content: "";
			position: absolute;
			left: 0;
			top: 1.25rem;
			bottom: 1.25rem;
			width: 3px;
			border-radius: 999px;
			background: ${(p) =>
				p.$featured
					? `linear-gradient(180deg, ${theme.color.primary}, rgba(0, 119, 204, 0.35))`
					: `linear-gradient(180deg, ${theme.color.primary}, rgba(0, 119, 204, 0.2))`};
		}
	}
`;

const IconRing = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 2.75rem;
	height: 2.75rem;
	border-radius: ${theme.radius.full};
	margin-bottom: 1rem;
	background: ${(p) => (p.$featured ? "rgba(0, 119, 204, 0.22)" : "rgba(0, 119, 204, 0.18)")};
	border: 1px solid ${(p) => (p.$featured ? "rgba(0, 119, 204, 0.5)" : "rgba(0, 119, 204, 0.35)")};
	color: ${theme.color.primary};
	box-shadow: 0 0 20px ${(p) => (p.$featured ? "rgba(0, 119, 204, 0.35)" : "rgba(0, 119, 204, 0.2)")};

	@media (min-width: 900px) {
		margin-left: auto;
		margin-right: auto;
	}
`;

const FeaturedBadge = styled.span`
	display: inline-flex;
	align-self: flex-start;
	margin-bottom: 0.65rem;
	padding: 0.25rem 0.55rem;
	border-radius: ${theme.radius.full};
	font-family: ${theme.fontFamily.sans};
	font-size: 0.6875rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.07em;
	color: #fff;
	background: ${theme.color.primary};
`;

const StepAccent = styled.p`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.75rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: ${(p) => (p.$featured ? theme.color.primary : "rgba(232, 238, 244, 0.55)")};
	margin: 0 0 0.35rem;
`;

const StepTitle = styled.h3`
	font-family: ${theme.fontFamily.sans};
	font-size: ${(p) => (p.$featured ? "1.25rem" : "1.0625rem")};
	font-weight: 800;
	margin: 0 0 0.5rem;
	color: #fff;
	letter-spacing: -0.02em;
`;

const StepBody = styled.p`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.875rem;
	color: ${theme.color.onDarkMuted};
	margin: 0;
	line-height: 1.6;
	flex: 1;
`;

const ClosingStrong = styled.strong`
	font-weight: 700;
	color: #fff;
`;

const ClosingNote = styled.p`
	margin: 1.35rem 0 0;
	padding-top: 1.25rem;
	border-top: 1px solid rgba(255, 255, 255, 0.1);
	font-family: ${theme.fontFamily.sans};
	font-size: 0.9375rem;
	line-height: 1.6;
	color: rgba(232, 238, 244, 0.72);
	max-width: 52rem;
`;

const ICONS = {
	quality: MdCheckCircle,
	speed: MdFlashOn,
	value: MdMonetizationOn,
	deliver: MdDeviceHub,
};

export default function ProofLoop() {
	return (
		<ProofSection id={sectionIds.proofLoop} $inverse aria-labelledby="proof-loop-heading">
			<Inner>
				<Header>
					<Eyebrow>{proofLoop.eyebrow}</Eyebrow>
					<Title id="proof-loop-heading">{proofLoop.title}</Title>
					<Lead>{proofLoop.lead}</Lead>
				</Header>

				<Pipeline>
					<FlowRail aria-hidden />
					<Steps>
						{proofLoop.steps.map((step) => {
							const Icon = ICONS[step.icon] || MdCheckCircle;
							const featured = Boolean(step.featured);
							return (
								<StepCard key={step.key} $featured={featured}>
									{featured ? <FeaturedBadge>Compare the invoice</FeaturedBadge> : null}
									<IconRing $featured={featured} aria-hidden>
										<Icon size={22} />
									</IconRing>
									<StepAccent $featured={featured}>{step.accent}</StepAccent>
									<StepTitle $featured={featured}>{step.label}</StepTitle>
									<StepBody>{step.body}</StepBody>
								</StepCard>
							);
						})}
					</Steps>
				</Pipeline>

				{proofLoop.leadDetail ? (
					<ClosingNote>
						{proofLoop.leadDetail.before}
						<ClosingStrong>{proofLoop.leadDetail.bold}</ClosingStrong>
						{proofLoop.leadDetail.after}
					</ClosingNote>
				) : null}
			</Inner>
		</ProofSection>
	);
}
